/**
 * Base OpenStreetMap (Leaflet) da Topologia → Mapa: alternativa ao Google Maps, escolhida pelo usuário na própria tela.
 *
 * Desenha o mesmo que o mapa do Google: POPs (bolinha verde com devices, cinza sem; maior com zoom) e enlaces na cor do
 * estado, tracejados quando pendentes de aprovação e pulsando quando DOWN. O traçado (pathGeometry ou arco dos
 * paralelos) vem pronto do MapPage, para os dois mapas mostrarem a mesma coisa. Popup do POP, edição de enlace, submapa
 * e painel lateral continuam no MapPage. Tiles: tile.openstreetmap.org (CSP img-src https: já libera).
 */
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Pop, PopLink } from '../api';

type Pt = { lat: number; lng: number };

export type OsmProps = {
  pops: Pop[];
  links: { link: PopLink; path: Pt[] }[];
  corDoEstado: (state: string) => string;
  onSelectPop: (p: Pop) => void;
  onClickLink: (l: PopLink) => void;
  encaixe: { pts: Pt[]; chave: string };      // fitBounds nos POPs quando a chave muda
  encaixeEnlace: { pts: Pt[]; chave: string }; // fitBounds no traçado de um enlace (alarme do painel)
  foco: { alvo: (Pt & { zoom?: number }) | null; chave: number };
};

const raioPop = (zoom: number) => (zoom <= 7 ? 4 : zoom === 8 ? 5 : zoom === 9 ? 6 : zoom <= 11 ? 8 : 10);

export function MapaOsm({ pops, links, corDoEstado, onSelectPop, onClickLink, encaixe, encaixeEnlace, foco }: OsmProps) {
  const divRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const camadaLinks = useRef<L.LayerGroup | null>(null);
  const camadaPops = useRef<L.LayerGroup | null>(null);
  // callbacks mais recentes sem recriar as camadas
  const cb = useRef({ onSelectPop, onClickLink });
  cb.current = { onSelectPop, onClickLink };

  // Mapa: uma vez
  useEffect(() => {
    if (!divRef.current) return;
    const m = L.map(divRef.current, { center: [-9.7, -36.7], zoom: 7, zoomControl: true, preferCanvas: false, worldCopyJump: true });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      // o nginx do NMS manda Referrer-Policy: no-referrer e o OSM bloqueia tile sem Referer (403 "Access blocked")
      referrerPolicy: 'origin',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>'
    }).addTo(m);
    camadaLinks.current = L.layerGroup().addTo(m);
    camadaPops.current = L.layerGroup().addTo(m);
    mapRef.current = m;
    // o container muda de tamanho (tela cheia, painel): Leaflet precisa saber
    const ro = new ResizeObserver(() => m.invalidateSize());
    ro.observe(divRef.current);
    return () => { ro.disconnect(); m.remove(); mapRef.current = null; };
  }, []);

  // Enlaces
  useEffect(() => {
    const g = camadaLinks.current;
    if (!g) return;
    g.clearLayers();
    const timers: number[] = [];
    for (const { link, path } of links) {
      const pendente = link.approved === false;
      const cor = corDoEstado(link.state);
      const linha = L.polyline(path.map((p) => [p.lat, p.lng] as [number, number]), {
        color: cor, weight: 3, opacity: 0.85, dashArray: pendente ? '6 6' : undefined, interactive: true
      });
      linha.on('click', () => cb.current.onClickLink(link));
      linha.bindTooltip(`${link.sourcePop.name} ↔ ${link.destPop.name}`, { sticky: true });
      // DOWN pulsa, como no Google (telão do NOC)
      if (link.state === 'DOWN' && !pendente) {
        let apagado = false;
        timers.push(window.setInterval(() => { apagado = !apagado; linha.setStyle({ opacity: apagado ? 0.25 : 0.95, weight: apagado ? 3 : 4 }); }, 600));
      }
      linha.addTo(g);
    }
    return () => { for (const t of timers) window.clearInterval(t); };
  }, [links, corDoEstado]);

  // POPs (tamanho pelo zoom)
  useEffect(() => {
    const m = mapRef.current, g = camadaPops.current;
    if (!m || !g) return;
    const marcas: { c: L.CircleMarker }[] = [];
    g.clearLayers();
    for (const p of pops) {
      if (p.latitude == null || p.longitude == null) continue;
      const cor = (p._count?.devices ?? 0) > 0 ? '#10b981' : '#64748b';
      const c = L.circleMarker([p.latitude, p.longitude], { radius: raioPop(m.getZoom()), color: '#0f172a', weight: 1.5, fillColor: cor, fillOpacity: 1 });
      c.bindTooltip(`${p.uf ? `[${p.uf}] ` : ''}${p.name}`, { direction: 'top', offset: [0, -6] });
      c.on('click', () => cb.current.onSelectPop(p));
      c.addTo(g);
      marcas.push({ c });
    }
    const zoom = () => { const r = raioPop(m.getZoom()); for (const { c } of marcas) c.setRadius(r); };
    m.on('zoomend', zoom);
    return () => { m.off('zoomend', zoom); };
  }, [pops]);

  // Encaixar: todos os POPs ou o traçado de um enlace
  const encaixar = (pts: Pt[]) => {
    const m = mapRef.current;
    if (!m || pts.length < 1) return;
    if (pts.length === 1) m.setView([pts[0].lat, pts[0].lng], 12);
    else m.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng] as [number, number])), { padding: [40, 40] });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => encaixar(encaixe.pts), [encaixe.chave]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => encaixar(encaixeEnlace.pts), [encaixeEnlace.chave]);

  // Focar num ponto (busca do painel lateral)
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !foco.alvo) return;
    m.setView([foco.alvo.lat, foco.alvo.lng], foco.alvo.zoom ?? Math.max(m.getZoom(), 13));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foco.chave]);

  return <div ref={divRef} style={{ width: '100%', height: '100%', background: '#e5e7eb' }} />;
}
